import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  BookOpen,
  ChevronDown,
  ChevronLeft,
  FileVideo,
  GraduationCap,
  LayoutDashboard,
  LineChart,
  MessageSquare,
  Settings,
  ShoppingCart,
  Sparkles,
  Store,
  Users,
  Wallet,
} from 'lucide-react';
import { cn } from '@mlm/ui';
import { getSiteManifest } from '@mlm/shared';
import { getNavCounts, getTenantFeatures, type NavCounts } from '../../lib/api';
import { useNavCountsSocket } from '../../lib/nav-counts-socket';
import { useAuthStore } from '../../lib/auth-store';

/** Sidebar routes this build's tenant manifest declares unused (its bespoke
 * site never renders the surfaces they curate). Cosmetic; routes still work. */
const MANIFEST_HIDDEN_NAV = new Set(
  getSiteManifest(import.meta.env.VITE_TENANT as string | undefined)?.adminHide?.sidebarItems ?? [],
);

/** Routes that only exist behind the `coaching_enabled` master switch. */
const COACHING_NAV = new Set(['/coaching']);

/** Sub-routes that only exist behind the `lms_enabled` master switch. */
const LMS_NAV = new Set([
  '/creators',
  '/moderation',
  '/course-topics',
  '/course-tags',
  '/course-enrollments',
  '/certificate-templates',
  '/creator-payout-fees',
  '/learning-pages',
  '/learning-settings',
]);

/**
 * Phase 12.5 admin shell — left rail with expandable sections.
 *
 * Each top-level item has a default `to` (where clicking the row navigates)
 * plus an optional `children` array of sub-routes that render below the
 * parent when the section is expanded. Sections auto-expand when any of
 * their routes is active, and the open/closed state for non-active
 * sections persists in localStorage.
 *
 * Per-leaf view permissions hide sub-items the user can't access. A
 * parent with no visible children is hidden too. super_admin bypasses
 * all checks. Permissions are baked into the JWT at login — changes
 * apply on the user's next refresh.
 */

type SubItem = {
  label: string;
  to: string;
  perm?: string;
  superAdmin?: boolean;
  /** Queue this leaf shows a count for; see `NavCounts`. */
  badge?: NavCountKey;
};

type SectionItem = {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  /** Routes that should mark this section active (exact OR prefix match). */
  match: string[];
  children?: SubItem[];
  /** Perm required for flat (no-children) items. */
  perm?: string;
  /** Hard super-admin gate — hidden from everyone but super_admin. */
  superAdmin?: boolean;
  /** Queue a FLAT item shows a count for. A parent sums its children's. */
  badge?: NavCountKey;
};

type NavCountKey = keyof NavCounts;

const ITEMS: SectionItem[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard, match: ['/'] },
  {
    label: 'Members',
    to: '/members',
    icon: Users,
    match: ['/members', '/kyc-review', '/archive-requests', '/slots', '/memberships', '/access'],
    children: [
      { label: 'Users', to: '/members', perm: 'view.members.users' },
      { label: 'KYC Review', to: '/kyc-review', perm: 'view.members.users', badge: 'kyc' },
      // Account erasure. Deliberately NOT in LMS_NAV — a member asking to be
      // deleted has nothing to do with whether the tenant sells courses.
      {
        label: 'Archive requests',
        to: '/archive-requests',
        perm: 'view.members.archive',
        badge: 'archiveRequests',
      },
      { label: 'Slots', to: '/slots', perm: 'view.members.slots' },
      { label: 'Memberships', to: '/memberships', perm: 'view.members.memberships' },
      { label: 'Access', to: '/access', perm: 'view.members.access' },
    ],
  },
  {
    label: 'Commerce',
    to: '/orders',
    icon: ShoppingCart,
    match: [
      '/orders',
      '/products',
      '/virtual-products',
      '/materials',
      '/categories',
      '/variation-types',
      '/suppliers',
      '/purchase-orders',
      '/coupons',
      '/gift-checks',
      '/flash-sale',
      '/landing-packages',
      '/featured-packages',
      '/branches',
      '/expiring-stock',
      '/shipping',
      '/merchants',
      '/ecom-payment-methods',
      '/pos-payment-methods',
    ],
    children: [
      { label: 'Orders', to: '/orders', perm: 'order.read', badge: 'orders' },
      { label: 'Products', to: '/products', perm: 'view.products.catalog' },
      // Services / digital goods — same permission, separate catalog. The two
      // lists partition the products table, so nothing shows up in both.
      // Dropped entirely unless the tenant enables virtual products (below).
      { label: 'Virtual Products', to: '/virtual-products', perm: 'view.products.catalog' },
      // Supplies that are stocked but never sold (cups, lids); recipes use them
      // up. A third partition of the same table, dropped unless the tenant
      // enables materials (below).
      { label: 'Materials', to: '/materials', perm: 'view.products.catalog' },
      { label: 'Categories', to: '/categories', perm: 'view.products.catalog' },
      { label: 'Variation Types', to: '/variation-types', perm: 'view.products.catalog' },
      { label: 'Suppliers', to: '/suppliers', perm: 'view.products.suppliers' },
      { label: 'Purchase Orders', to: '/purchase-orders', perm: 'view.products.purchase_orders' },
      { label: 'Discounts', to: '/coupons', perm: 'view.products.coupons' },
      // Earned or hand-issued one-time discounts. Dropped unless the tenant
      // turns gift checks on (below), and renamed to the tenant's own word.
      { label: 'Gift Checks', to: '/gift-checks', perm: 'view.products.coupons' },
      { label: 'Flash Sale', to: '/flash-sale', perm: 'view.settings.tenant' },
      { label: 'Landing Packages', to: '/landing-packages', perm: 'view.settings.tenant' },
      { label: 'Featured Packages', to: '/featured-packages', perm: 'view.settings.tenant' },
      { label: 'Branches', to: '/branches', perm: 'view.branch.branches' },
      // Stock lots: deliveries about to expire. Dropped unless the tenant
      // tracks stock in lots (below).
      { label: 'Expiring Stock', to: '/expiring-stock', perm: 'view.branch.branches' },
      { label: 'Shipping', to: '/shipping', perm: 'view.branch.shipping' },
      {
        label: 'Payment methods',
        to: '/ecom-payment-methods',
        perm: 'view.commerce.payment_methods',
      },
      {
        label: 'POS payment methods',
        to: '/pos-payment-methods',
        perm: 'view.commerce.payment_methods',
      },
      { label: 'Merchants', to: '/merchants', perm: 'view.merchants.accounts' },
    ],
  },
  {
    // Every LMS surface, in one place. These seven were previously scattered
    // across four sections — Creators under Commerce, Creator Fees under
    // Finance, the four course-catalog pages interleaved into Content, and
    // Moderation as a top-level row of its own — which made "where is the LMS?"
    // a question with four answers.
    //
    // Paths are deliberately UNCHANGED. They are load-bearing outside this file:
    // `pages/moderation.tsx` links to `/creators?tab=courses`, and the API mints
    // notification deep links at `services/lms-notifications.ts`. Relabelling is
    // free; re-pathing breaks the bell, and the router's catch-all would swallow
    // the mistake by bouncing to the dashboard rather than 404ing.
    label: 'Learning',
    to: '/creators',
    icon: GraduationCap,
    // Every path here is in LMS_NAV, so the whole section disappears with the
    // `lms_enabled` master switch: the filter drops a parent once all of its
    // children have been filtered out.
    match: [
      '/creators',
      '/moderation',
      '/course-topics',
      '/course-tags',
      '/course-enrollments',
      '/certificate-templates',
      '/creator-payout-fees',
      '/learning-pages',
      '/learning-settings',
    ],
    children: [
      { label: 'Creators', to: '/creators', perm: 'view.creators.accounts' },
      { label: 'Course Topics', to: '/course-topics', perm: 'view.products.catalog' },
      { label: 'Course Tags', to: '/course-tags', perm: 'view.products.catalog' },
      { label: 'Course Enrollments', to: '/course-enrollments', perm: 'view.products.catalog' },
      {
        label: 'Certificate Templates',
        to: '/certificate-templates',
        perm: 'view.products.catalog',
      },
      { label: 'Moderation', to: '/moderation', perm: 'view.moderation' },
      { label: 'Creator Fees & Tax', to: '/creator-payout-fees', perm: 'view.payouts.fees' },
      { label: 'Learning Pages', to: '/learning-pages', perm: 'view.settings.tenant' },
      { label: 'Learning Settings', to: '/learning-settings', perm: 'view.settings.tenant' },
    ],
  },
  {
    // Miracles Protocol. In COACHING_NAV, so the whole entry disappears with
    // the `coaching_enabled` master switch — a tenant admin must not learn the
    // coach app exists from the rail.
    label: 'Coaching',
    to: '/coaching',
    icon: Sparkles,
    match: ['/coaching'],
    perm: 'view.coaching',
  },
  {
    label: 'Messages',
    to: '/messages',
    icon: MessageSquare,
    match: ['/messages'],
    perm: 'view.messages',
    badge: 'messages',
  },
  {
    label: 'Feedback',
    to: '/feedback',
    icon: MessageSquare,
    match: ['/feedback'],
    perm: 'view.feedback',
  },
  {
    label: 'Finance',
    to: '/payments',
    icon: Wallet,
    // `match` drives BOTH the active highlight and the auto-expand, so a new
    // child must be listed here as well as in `children`.
    match: ['/payments', '/wallet-topups', '/payouts', '/payout-fees', '/merchant-payout-fees'],
    children: [
      { label: 'Payments', to: '/payments', perm: 'view.payments', badge: 'payments' },
      { label: 'Wallet Top-Ups', to: '/wallet-topups', perm: 'topup.review', badge: 'topups' },
      { label: 'Payouts', to: '/payouts', perm: 'view.payouts.requests', badge: 'payouts' },
      { label: 'Fees & Tax', to: '/payout-fees', perm: 'view.payouts.fees' },
      { label: 'Merchant Fees & Tax', to: '/merchant-payout-fees', perm: 'view.payouts.fees' },
    ],
  },
  {
    label: 'Compensation',
    to: '/compplan',
    icon: BarChart3,
    match: [
      '/compplan',
      '/periods',
      '/distribute',
      '/distribution-runs',
      '/distributions',
      '/vortex',
    ],
    children: [
      { label: 'Plans', to: '/compplan', perm: 'view.compensation.compplan' },
      { label: 'Periods', to: '/periods', perm: 'view.compensation.periods' },
      { label: 'Unilevel', to: '/distribute', perm: 'view.compensation.distribute' },
      {
        label: 'Distribution Runs',
        to: '/distribution-runs',
        perm: 'view.compensation.distribute',
      },
      { label: 'Distributions', to: '/distributions', perm: 'view.compensation.distributions' },
      { label: 'Vortex Monitor', to: '/vortex', perm: 'view.compensation.vortex' },
    ],
  },
  {
    label: 'Storefront',
    to: '/storefront',
    icon: Store,
    match: ['/storefront', '/layout-theme', '/official-feedback'],
    children: [
      { label: 'Content', to: '/storefront', perm: 'view.settings.tenant' },
      { label: 'Layout & Theme', to: '/layout-theme', superAdmin: true },
      { label: 'Official Store Feedback', to: '/official-feedback', perm: 'view.products.catalog' },
    ],
  },
  {
    label: 'Content',
    to: '/training-videos',
    icon: FileVideo,
    match: [
      '/training-videos',
      '/resources',
      '/replicated-page',
      '/announcements',
      '/special-links',
    ],
    children: [
      { label: 'Training Videos', to: '/training-videos', perm: 'view.assets.training_videos' },
      { label: 'Resources', to: '/resources', perm: 'view.assets.resources' },
      // Testimonials moved into the product editor (per-product "Testimonials" tab) —
      // they're per-product data, not a global CMS surface.
      { label: 'Replicated Pages', to: '/replicated-page', perm: 'view.content.replicated' },
      { label: 'Announcements', to: '/announcements', perm: 'view.announcements' },
      { label: 'Special Links', to: '/special-links', perm: 'view.assets.training_videos' },
    ],
  },
  {
    label: 'Reports',
    to: '/reports',
    icon: LineChart,
    match: ['/reports'],
    perm: 'view.reports',
    children: [
      { label: 'Overview', to: '/reports', perm: 'view.reports' },
      { label: 'Stockist Receivables', to: '/reports/stockist-receivables', perm: 'view.reports' },
    ],
  },
  {
    label: 'Settings',
    to: '/settings',
    icon: Settings,
    match: ['/settings', '/audit', '/dlq', '/data-import'],
    children: [
      { label: 'System', to: '/settings', perm: 'view.settings.tenant' },
      { label: 'Audit log', to: '/audit', perm: 'view.settings.audit' },
      // Failed-job recovery — a diagnostics surface alongside the audit log, not
      // a payouts concern. Perm code stays `view.payouts.dlq` (renaming a seeded
      // permission is a separate DB change); only the nav home moved.
      { label: 'Failed jobs (DLQ)', to: '/dlq', perm: 'view.payouts.dlq' },
      // Super-admin only: bulk import from a legacy Firestore .xlsx export.
      { label: 'Legacy Import', to: '/data-import', superAdmin: true },
    ],
  },
];

/**
 * Accounting section — injected after Finance ONLY when the tenant's
 * `accounting_enabled` feature flag is on (Settings → System toggle).
 * Every page is LIVE (Accounting v1 + the Phase 4 lean GL) and perm-gated on
 * `view.accounting` (Consignment on `consignment.read`, and additionally on
 * `consignment_enabled` — filtered below). The old sample-data previews are
 * gone; sample-data.ts survives only for its invariants test.
 */
const ACCOUNTING_ITEM: SectionItem = {
  label: 'Accounting',
  to: '/accounting',
  icon: BookOpen,
  match: ['/accounting'],
  children: [
    // R2 — gated on the `view.accounting.ar` sub-leaf, so a receivables clerk
    // can hold the customer master without the payables/GL surfaces.
    { label: 'Customers', to: '/accounting/customers', perm: 'view.accounting.ar' },
    // Phase C. Hidden unless `ar_invoicing_enabled` — the flag gates raising a
    // billing, so the page has nothing to offer a tenant that has it off.
    { label: 'Billings', to: '/accounting/ar', perm: 'view.accounting.ar' },
    // Phase D. Same flag and view leaf as Billings — collecting is the other
    // half of the same desk.
    { label: 'Payments', to: '/accounting/ar/payments', perm: 'view.accounting.ar' },
    // Phase E. Same flag and view leaf: credit given back is the same desk.
    { label: 'Credit Memos', to: '/accounting/ar/credit-memos', perm: 'view.accounting.ar' },
    { label: 'Payables', to: '/accounting/payables', perm: 'view.accounting' },
    { label: 'Returns to Supplier', to: '/accounting/supplier-returns', perm: 'view.accounting' },
    { label: 'Expenses', to: '/accounting/expenses', perm: 'view.accounting' },
    { label: 'Cash & Bank', to: '/accounting/funds', perm: 'view.accounting' },
    { label: 'Consignment', to: '/accounting/consignment', perm: 'consignment.read' },
    {
      label: 'Stockist Consignment',
      to: '/accounting/stockist-consignment',
      perm: 'stockist_consignment.read',
    },
    { label: 'Overview', to: '/accounting', perm: 'view.accounting' },
    { label: 'Chart of Accounts', to: '/accounting/accounts', perm: 'view.accounting' },
    { label: 'Journal', to: '/accounting/journal', perm: 'view.accounting' },
    { label: 'Statements', to: '/accounting/statements', perm: 'view.accounting' },
  ],
};

const EXPAND_KEY = 'mlm.admin.sidebar.expanded';

function isSectionActive(item: SectionItem, pathname: string): boolean {
  return item.match.some((m) =>
    m === '/' ? pathname === '/' : pathname === m || pathname.startsWith(`${m}/`),
  );
}

function isChildActive(child: SubItem, pathname: string): boolean {
  return pathname === child.to || pathname.startsWith(`${child.to}/`);
}

function loadExpanded(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(EXPAND_KEY) ?? '{}');
  } catch {
    return {};
  }
}

/**
 * The pill. Absent (not zero) when there is nothing waiting, and absent when the
 * viewer can't read that queue — `NavCounts` omits those, so `undefined` and
 * `0` both correctly render nothing.
 *
 * Clamped at 99+ like the notification bell: past that the exact number stops
 * being information and starts breaking the rail's layout.
 */
function CountBadge({ n }: { n: number | undefined }) {
  if (!n || n <= 0) return null;
  return (
    <span className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10.5px] font-semibold leading-none text-white tabular-nums">
      {n > 99 ? '99+' : n}
    </span>
  );
}

/**
 * What a PARENT row shows while collapsed: the sum of its children's queues, so
 * work is never hidden behind a shut section. A flat item just uses its own.
 */
function sectionCount(item: SectionItem, counts: NavCounts): number {
  if (item.badge) return counts[item.badge] ?? 0;
  return (item.children ?? []).reduce((n, c) => n + (c.badge ? (counts[c.badge] ?? 0) : 0), 0);
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(loadExpanded);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = !!user?.roles.includes('super_admin');

  // Same cache entry the Settings page uses, so flipping the System toggle
  // updates the rail immediately. A staff account whose role can't read
  // tenant features just doesn't get the section (error → hidden).
  const featuresQuery = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
    staleTime: 60_000,
    retry: false,
  });
  /**
   * Queue depths for the badges. The socket below pushes a refetch signal the
   * moment a queue moves, so the interval here is a SAFETY NET, not the
   * mechanism — deliberately slow. It is also the only thing that covers the
   * worker, which shares no IO instance with the API and so cannot emit for the
   * sweeps it owns (auto-complete, autoship, payout batches).
   *
   * `retry: false` matches the features query above: a staff account that 403s
   * simply gets no badges rather than a retry storm.
   */
  const navCountsQuery = useQuery({
    queryKey: ['nav-counts'],
    queryFn: getNavCounts,
    staleTime: 30_000,
    refetchInterval: 300_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  useNavCountsSocket(!!user);
  const counts: NavCounts = navCountsQuery.data ?? {};

  const accountingEnabled = featuresQuery.data?.accounting_enabled === true;
  // Accounting v1: the cashier-side consignment module's admin views only
  // exist for tenants that opted into consignment.
  const consignmentEnabled = featuresQuery.data?.consignment_enabled === true;
  const arInvoicingEnabled = featuresQuery.data?.ar_invoicing_enabled === true;
  const stockistConsignmentEnabled = featuresQuery.data?.stockist_consignment_enabled === true;
  // Master switch for the virtual-products feature. A tenant selling only
  // physical goods never sees the entry. Same cache entry as Settings, so the
  // rail updates the moment the toggle is saved.
  const virtualProductsEnabled = featuresQuery.data?.virtual_products_enabled === true;
  // Same for materials and recipes: a tenant that only resells finished goods
  // never sees the entry.
  const materialsEnabled = featuresQuery.data?.materials_enabled === true;
  // And for stock lots: no lots, no expiry dates, nothing to list.
  const stockLotsEnabled = featuresQuery.data?.inventory_lots_enabled === true;
  // Gift checks, under whatever the tenant calls them.
  const vouchersEnabled = featuresQuery.data?.vouchers_enabled === true;
  const voucherLabel = featuresQuery.data?.voucher_label?.trim() || 'Gift Check';
  const voucherNavLabel = voucherLabel.endsWith('s') ? voucherLabel : `${voucherLabel}s`;
  // LMS master switch: a tenant with learning off never sees the Creators
  // entry (tenant admins must not learn the feature exists from the rail).
  const lmsEnabled = featuresQuery.data?.lms_enabled === true;
  // Coaching master switch, for the same reason.
  const coachingEnabled = featuresQuery.data?.coaching_enabled === true;

  function canSee(perm: string | undefined): boolean {
    if (!perm) return true;
    if (isSuperAdmin) return true;
    return !!user?.perms.includes(perm);
  }

  // Combined gate: a `superAdmin` item is hidden from everyone but super_admin
  // (a hard role gate that granting a perm can't open); otherwise perm-based.
  function canSeeItem(it: { perm?: string; superAdmin?: boolean }): boolean {
    if (it.superAdmin && !isSuperAdmin) return false;
    return canSee(it.perm);
  }

  // Accounting slots in right after Finance when the tenant flag is on.
  const allItems: SectionItem[] = accountingEnabled
    ? ITEMS.flatMap((item) => (item.label === 'Finance' ? [item, ACCOUNTING_ITEM] : [item]))
    : ITEMS;

  // Filter items down to what the user is allowed to see. Drop sub-items the
  // user has no perm for. A section with children loses entirely if all its
  // children are gone — the parent without any child has nothing to lead to.
  const visibleItems: SectionItem[] = allItems.flatMap((item) => {
    if (item.children) {
      const visibleChildren = item.children.filter(
        (c) =>
          canSeeItem(c) &&
          !MANIFEST_HIDDEN_NAV.has(c.to) &&
          (c.to !== '/virtual-products' || virtualProductsEnabled) &&
          (c.to !== '/materials' || materialsEnabled) &&
          (c.to !== '/expiring-stock' || stockLotsEnabled) &&
          (c.to !== '/gift-checks' || vouchersEnabled) &&
          (!LMS_NAV.has(c.to) || lmsEnabled) &&
          (!COACHING_NAV.has(c.to) || coachingEnabled) &&
          (c.to !== '/accounting/consignment' || consignmentEnabled) &&
          (!c.to.startsWith('/accounting/ar') || arInvoicingEnabled) &&
          (c.to !== '/accounting/stockist-consignment' || stockistConsignmentEnabled),
      );
      if (visibleChildren.length === 0) return [];
      const named = visibleChildren.map((c) =>
        c.to === '/gift-checks' ? { ...c, label: voucherNavLabel } : c,
      );
      // The header keeps its own target when that page is one of the visible
      // children (Accounting → Overview, which sits LAST in its list), and
      // otherwise re-points to the first VISIBLE child so clicking the parent
      // never lands on a forbidden page. Every other group's own target is its
      // first child anyway.
      const home = named.find((c) => c.to === item.to) ?? named[0]!;
      return [{ ...item, to: home.to, children: named }];
    }
    // No flat item is in LMS_NAV today — Moderation, the one that was, is now a
    // child of Learning. Kept as a guard rather than deleted as dead code: a
    // future flat LMS route added without it would advertise the feature on
    // tenants that have the master switch off.
    if (LMS_NAV.has(item.to) && !lmsEnabled) return [];
    if (COACHING_NAV.has(item.to) && !coachingEnabled) return [];
    return canSeeItem(item) ? [item] : [];
  });

  // Auto-expand the active section so the user always sees where they are.
  useEffect(() => {
    const active = visibleItems.find((i) => i.children && isSectionActive(i, pathname));
    if (active && !expanded[active.label]) {
      setExpanded((prev) => ({ ...prev, [active.label]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    window.localStorage.setItem(EXPAND_KEY, JSON.stringify(expanded));
  }, [expanded]);

  function toggleSection(label: string) {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  return (
    <aside
      className={cn(
        'h-screen sticky top-0 border-r bg-card flex flex-col transition-[width] duration-150',
        collapsed ? 'w-[64px]' : 'w-[240px]',
      )}
    >
      <div
        className={cn(
          'flex items-center h-14 border-b px-4',
          collapsed ? 'justify-center' : 'justify-between',
        )}
      >
        {!collapsed ? (
          <span className="font-semibold text-sm tracking-tight">MLM.Admin</span>
        ) : null}
        <button
          type="button"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="text-muted-foreground hover:text-foreground transition-colors"
          onClick={onToggleCollapsed}
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5 px-2">
          {visibleItems.map((item) => {
            const sectionActive = isSectionActive(item, pathname);
            const hasChildren = !!item.children?.length;
            const isOpen = !collapsed && hasChildren && (sectionActive || expanded[item.label]);
            return (
              <li key={item.label}>
                <div className={cn('flex items-stretch')}>
                  <NavLink
                    to={item.to}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors flex-1',
                      collapsed && 'justify-center px-0',
                      sectionActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <span className="relative shrink-0">
                      <item.icon className="h-4 w-4 shrink-0" />
                      {/* Collapsed to the icon rail there is no room for a
                          number, but hiding the signal entirely would make the
                          rail lie about pending work — so a dot stands in. */}
                      {collapsed && sectionCount(item, counts) > 0 ? (
                        <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
                      ) : null}
                    </span>
                    {!collapsed ? <span className="flex-1">{item.label}</span> : null}
                    {/* On an OPEN section the children carry their own badges,
                        so summing here too would double-count in the reader's
                        eye. Show the roll-up only while it is shut. */}
                    {!collapsed && !isOpen ? <CountBadge n={sectionCount(item, counts)} /> : null}
                  </NavLink>
                  {hasChildren && !collapsed ? (
                    <button
                      type="button"
                      aria-label={`Toggle ${item.label}`}
                      onClick={() => toggleSection(item.label)}
                      className={cn(
                        'px-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors',
                      )}
                    >
                      <ChevronDown
                        className={cn('h-4 w-4 transition-transform', !isOpen && '-rotate-90')}
                      />
                    </button>
                  ) : null}
                </div>
                {hasChildren && isOpen ? (
                  <ul className="mt-0.5 ml-7 mb-1 border-l border-border pl-3 space-y-0.5">
                    {item.children!.map((child, _i, siblings) => {
                      // Only the MOST specific match lights up: a prefix match
                      // alone lit /accounting (Overview) and /accounting/ar
                      // (Billings) on every page beneath them.
                      const childActive =
                        isChildActive(child, pathname) &&
                        !siblings.some(
                          (s) => s.to.length > child.to.length && isChildActive(s, pathname),
                        );
                      return (
                        <li key={child.to}>
                          <NavLink
                            to={child.to}
                            className={cn(
                              'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors',
                              childActive
                                ? 'text-primary font-medium'
                                : 'text-muted-foreground hover:text-foreground',
                            )}
                          >
                            <span className="flex-1">{child.label}</span>
                            {child.badge ? <CountBadge n={counts[child.badge]} /> : null}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
